import {
  List,
  Datagrid,
  TextField,
  DateField,
  useCreate,
  useDelete,
  SaveButton,
  SimpleForm,
  Toolbar,
  useRefresh,
  useNotify,
  Button,
  useRecordContext,
  ImageInput,
} from 'react-admin'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DeleteIcon from '@mui/icons-material/Delete'

import CustomImageField from '../../components/CustomImageField'

const S3_BUCKET_URL = process.env.REACT_APP_S3_URL || ''

// Image thumbnail component
const ImageThumbnail = () => {
  const record = useRecordContext()
  if (!record || !record.path) return <span>-</span>

  const imageUrl = record.path.startsWith('http')
    ? record.path
    : `${S3_BUCKET_URL}/${record.path}`

  return (
    <img
      src={imageUrl}
      alt={record.name || 'Image'}
      style={{
        width: 80,
        height: 80,
        objectFit: 'cover',
        borderRadius: 4,
      }}
    />
  )
}

// Path field with full URL
const PathField = () => {
  const record = useRecordContext()
  if (!record || !record.path) return <span>-</span>

  const fullUrl = record.path.startsWith('http')
    ? record.path
    : `${S3_BUCKET_URL}/${record.path}`

  return <span>{fullUrl}</span>
}

// Open in new tab button
const OpenImageButton = () => {
  const record = useRecordContext()
  if (!record || !record.url) return null

  const imageUrl = record.url.startsWith('http')
    ? record.url
    : `${S3_BUCKET_URL}/${record.url}`

  const handleClick = e => {
    e.stopPropagation()
    window.open(imageUrl, '_blank')
  }

  return (
    <Button label="Open" onClick={handleClick}>
      <OpenInNewIcon />
    </Button>
  )
}

// Delete image button
const DeleteImageButton = () => {
  const record = useRecordContext()
  const [deleteOne] = useDelete()
  const refresh = useRefresh()
  const notify = useNotify()

  const handleDelete = e => {
    e.stopPropagation()
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteOne(
        'image-storage',
        { id: record.id },
        {
          onSuccess: () => {
            refresh()
            notify('Image deleted successfully')
          },
          onError: error => {
            notify(`${error.message}`, { type: 'error' })
          },
        },
      )
    }
  }

  return (
    <Button label="Delete" onClick={handleDelete} color="error">
      <DeleteIcon />
    </Button>
  )
}

// Upload toolbar
const UploadToolbar = () => {
  return (
    <Toolbar>
      <SaveButton label="Upload Image" />
    </Toolbar>
  )
}

// Upload aside panel
const UploadAside = () => {
  const [create] = useCreate()
  const refresh = useRefresh()
  const notify = useNotify()

  const handleSubmit = data => {
    if (!data.imageFile) {
      notify('Please select an image to upload', { type: 'warning' })
      return
    }

    create(
      'image-storage',
      { data },
      {
        onSuccess: () => {
          refresh()
          notify('Image uploaded successfully')
        },
        onError: error => {
          notify(`Upload failed: ${error.message}`, { type: 'error' })
        },
      },
    )
  }

  return (
    <Box sx={{ margin: '0.5rem', width: 350 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Upload New Image
      </Typography>
      <SimpleForm toolbar={<UploadToolbar />} onSubmit={handleSubmit}>
        <ImageInput
          source="imageFile"
          label="Select Image"
          accept="image/*"
          multiple={false}>
          <CustomImageField source="src" title="Preview" />
        </ImageInput>
      </SimpleForm>
    </Box>
  )
}

const ImageStorageList = () => {
  return (
    <List
      resource="image-storage"
      aside={<UploadAside />}
      sort={{ field: 'createdAt', order: 'DESC' }}>
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <ImageThumbnail label="Preview" />
        <TextField source="name" label="File Name" />
        <PathField label="Path" />
        <DateField source="updatedAt" label="Updated At" />
        <DeleteImageButton label="Delete" />
      </Datagrid>
    </List>
  )
}

export default ImageStorageList
